import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * TextField — labeled input wrapper primitive.
 *
 * Sketched in Claude Design 2026-05-19 as part of the login surface.
 * Rationale: 44 px height (touch-target floor); 16 px padding (42 px
 * with leading icon); --radius-xl corners. Focus via --shadow-focus.
 * Combines label + optional leading icon + hint/error supplement.
 */
import * as React from 'react';
import { Icon } from './Icon.js';
import { cn } from './cn.js';
const SIZE_CLASSES = {
    md: 'min-h-touch px-3',
    lg: 'h-14 px-4',
};
// Login-surface inputs with a leading icon use a different left
// padding to clear the icon prefix (handoff-mandated `pl-[42px]`).
const SIZE_WITH_ICON_CLASSES = {
    md: 'min-h-touch pl-[42px] pr-3',
    lg: 'h-14 pl-[42px] pr-4',
};
const INPUT_BASE_CLASSES = [
    'block',
    'w-full',
    'rounded-xl',
    'border',
    'border-solid',
    'bg-[var(--bg-surface)]',
    'text-[15px]',
    'text-[var(--fg-primary)]',
    'outline-none',
    'focus-visible:border-[var(--border-focus)]',
    'placeholder:text-[var(--fg-tertiary)]',
    'disabled:opacity-50',
    'disabled:cursor-not-allowed',
].join(' ');
export const TextField = React.forwardRef(function TextField({ label, error, hint, icon, size = 'md', monospace = false, id, className, 'aria-describedby': ariaDescribedBy, 'aria-invalid': ariaInvalidProp, ...rest }, ref) {
    // Generate stable ids so the label/input/hint/error wiring works
    // without consumer plumbing. `useId` is React 18+; the kit
    // requires React 18 (see CLAUDE.md FE stack).
    const reactId = React.useId();
    const inputId = id ?? `${reactId}-input`;
    const hintId = hint !== undefined ? `${reactId}-hint` : undefined;
    const errorId = error !== undefined ? `${reactId}-error` : undefined;
    // Compose aria-describedby from consumer-supplied + hint + error.
    // Order: consumer-supplied first, then hint, then error — so
    // assistive tech reads the most important last (the error).
    const describedByTokens = [ariaDescribedBy, hintId, errorId].filter((token) => typeof token === 'string' && token.length > 0);
    const composedDescribedBy = describedByTokens.length > 0 ? describedByTokens.join(' ') : undefined;
    // Field is "invalid" if EITHER the kit-managed `error` prop is set
    // OR the consumer passes `aria-invalid={true}` explicitly (the MFA
    // submit-error pattern, where the error message lives outside this
    // field in an external live region). Both signals drive the
    // danger-border styling so the visual + semantic state stay in sync.
    const consumerSaysInvalid = ariaInvalidProp === true || ariaInvalidProp === 'true';
    const isInvalid = error !== undefined || consumerSaysInvalid;
    // Conflict-resolution rule for the DOM `aria-invalid` attribute:
    //   - If `error` is present, the kit-managed error state wins. The
    //     attribute is `true` regardless of any consumer-supplied
    //     `aria-invalid` (including `false`). This guarantees that
    //     visual invalid state (danger border + error paragraph) and
    //     semantic invalid state (`aria-invalid="true"`) stay aligned
    //     under all input combinations.
    //   - If no `error` is set, the consumer's explicit
    //     `aria-invalid` wins (including `false` — the MFA pattern's
    //     not-yet-invalid case). When the consumer also omits
    //     `aria-invalid`, no attribute is rendered.
    const ariaInvalidAttr = error !== undefined ? true : ariaInvalidProp;
    const sizeClass = (icon !== undefined ? SIZE_WITH_ICON_CLASSES : SIZE_CLASSES)[size];
    const inputClasses = cn(INPUT_BASE_CLASSES, sizeClass, monospace ? 'font-mono tracking-widest' : '', 
    // Border color: invalid state (from error OR consumer-supplied
    // aria-invalid) wins; otherwise default. Focus-visible override
    // is on INPUT_BASE_CLASSES above and applies regardless because
    // focus-visible is a higher-specificity utility state.
    isInvalid
        ? 'border-[var(--fg-danger)]'
        : 'border-[var(--border-default)]', className);
    // Structural note: the <label> wraps ONLY the visible label text
    // (associated with the input via `htmlFor`). Hint + error paragraphs
    // are siblings of the label/input pair, NOT descendants of the label.
    // This keeps the input's accessible name = the label text alone (not
    // label + error message), so RTL's `getByLabelText` and assistive
    // tech announce the field cleanly. The error wiring travels via
    // `aria-describedby`, not by being inside the label.
    return (_jsxs("div", { className: "mb-4 block", children: [_jsx("label", { htmlFor: inputId, className: "mb-2 block text-[13px] font-semibold text-[var(--fg-secondary)]", children: label }), icon !== undefined ? (_jsxs("span", { className: "relative block", children: [_jsx("span", { "aria-hidden": "true", className: "pointer-events-none absolute left-[14px] top-1/2 -translate-y-1/2 text-[var(--fg-tertiary)] inline-flex", children: _jsx(Icon, { name: icon, size: 20 }) }), _jsx("input", { ref: ref, id: inputId, "aria-invalid": ariaInvalidAttr, "aria-describedby": composedDescribedBy, className: inputClasses, ...rest })] })) : (_jsx("input", { ref: ref, id: inputId, "aria-invalid": ariaInvalidAttr, "aria-describedby": composedDescribedBy, className: inputClasses, ...rest })), hint !== undefined ? (_jsx("p", { id: hintId, className: "mt-2 text-xs text-[var(--fg-tertiary)]", children: hint })) : null, error !== undefined ? (_jsx("p", { id: errorId, role: "alert", className: "mt-2 text-xs text-[var(--fg-danger)]", children: error })) : null] }));
});
TextField.displayName = 'TextField';
//# sourceMappingURL=TextField.js.map